import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({
  currentPage,
  lastPage,
  perPage,
  total,
  onPageChange,
  className = '',
  showResultsCounter = true,
  scrollToTop = true,
}) => {
  if (lastPage <= 1) return null;

  const handlePageChange = (page) => {
    onPageChange(page);
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getVisiblePages = () => {
    const maxVisible = 5;
    const pages = [];

    if (lastPage <= maxVisible) {
      for (let i = 1; i <= lastPage; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...');
      pages.push(lastPage);
    } else if (currentPage >= lastPage - 2) {
      pages.push(1);
      pages.push('...');
      for (let i = lastPage - 3; i <= lastPage; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push('...');
      pages.push(lastPage);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={`mt-10 flex flex-wrap items-center justify-center gap-4 ${className}`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            currentPage === 1
              ? 'cursor-not-allowed border-m-border-light bg-m-surface text-m-ink-faint'
              : 'border-m-border-light bg-m-surface text-m-ink hover:border-m-ink/30'
          }`}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Previous
        </button>

        <div className="flex items-center gap-1">
          {visiblePages.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-2 text-m-ink-faint">
                  …
                </span>
              );
            }

            const isActive = page === currentPage;
            return (
              <button
                key={page}
                type="button"
                onClick={() => handlePageChange(page)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-m-ink text-white'
                    : 'border border-m-border-light bg-m-surface text-m-ink hover:border-m-ink/30'
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => handlePageChange(Math.min(lastPage, currentPage + 1))}
          disabled={currentPage === lastPage}
          className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            currentPage === lastPage
              ? 'cursor-not-allowed border-m-border-light bg-m-surface text-m-ink-faint'
              : 'border-m-border-light bg-m-surface text-m-ink hover:border-m-ink/30'
          }`}
        >
          Next
          <ChevronRight className="ml-1 h-4 w-4" />
        </button>
      </div>

      {showResultsCounter ? (
        <div className="text-sm text-m-ink-muted">
          Showing {(currentPage - 1) * perPage + 1} to{' '}
          {Math.min(currentPage * perPage, total)} of {total} results
        </div>
      ) : null}
    </div>
  );
};

export default Pagination;
